import { Component, Input, OnInit } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ChartType } from '@ddt-web/chart';
import { ColorsConfig } from '@ddt-web/config';
import { ChartConfig, ChartTableHeight, DisplayTableColumnClassOverride, NestedTableConfig } from '@ddt-web/data-layer-components';
import { ComponentData, TabData } from '@ddt-web/data-service';
import { MultiViewComponentType, NumberUtil } from '@ddt-web/generic-utils';
import * as flattenDeep from 'lodash/flattenDeep';
import * as range from 'lodash/range';
import * as round from 'lodash/round';

@Component({
	selector: 'risk-asset-allocation-projected-risk-return',
	templateUrl: './asset-allocation-projected-risk-return.component.html',
	styleUrls: ['./asset-allocation-projected-risk-return.component.scss']
})
export class AssetAllocationProjectedRiskReturnComponent implements OnInit {
	@Input() public tableDataTab: TabData;
	@Input() public chartData: ComponentData;
	@Input() public target: number;
	@Input() public projectionYearOffset: number; // the amount of years from which the projection is taken
	@Input() public columnClassOverride?: DisplayTableColumnClassOverride;
	@Input() public tableOptions?: NestedTableConfig;

	public readonly seriesColours = [ColorsConfig.PORTFOLIO_COLORS[1], ColorsConfig.PORTFOLIO_COLORS[0]];
	public readonly MultiViewComponentType = MultiViewComponentType;
	public readonly chartHeight: Array<ChartTableHeight> = [{ chartHeight: 160 }, { chartHeight: 200 }];

	public chartDataTabs: Array<TabData>;
	public returnChartConfig: ChartConfig;
	public riskChartConfig: ChartConfig;

	public hideChart: boolean;

	constructor(private contentService: ContentService) { }

	public ngOnInit(): void {
		this.chartDataTabs = [this.chartData?.getTabById('return.total'), this.chartData?.getTabById('risk.total')];
		this.columnClassOverride = this.columnClassOverride ?? this.getColumnClassesForTable(this.tableDataTab?.tableData[0]?.headersX?.length);

		const commonCustomAttributes = {
			shouldShowGridLine: true,
			shouldFormatValue: true,
			multiplier: 100,
			plotOptions: {
				series: {
					pointWidth: 20
				}
			},
			chart: {
				marginLeft: 150,
				marginBottom: undefined
			},
			autoTickPositions: false,
			numberOfTicks: 5,
			tickPositions: this.getTickPositions(5)
		};

		this.returnChartConfig = new ChartConfig(
			ChartType.Bar,
			'chart-with-tick-positions',
			{
				...commonCustomAttributes,
				legend: {
					enabled: false
				},
				title: {
					text: this.contentService.get('EXPECTED_RETURN', 'Expected Return (%)'),
					align: 'left',
					style: {
						fontSize: 14
					},
					x: 0,
					y: 100
				},
				tooltip: {
					formatter: this.getReturnChartTooltipFormatter()
				},
				auxiliaryData: this.createTooltipAuxiliaryData(this.chartData?.getTabById('auxiliary'))
			}
		);

		this.riskChartConfig = new ChartConfig(
			ChartType.Bar,
			'chart-with-tick-positions',
			{
				...commonCustomAttributes,
				legend: {
					enabled: true,
					margin: 20,
					floating: false,
					x: 135
				},
				title: {
					text: this.contentService.get('ESTIMATED_RISK', 'Estimated Risk (%)'),
					align: 'left',
					style: {
						fontSize: 14
					},
					x: 0,
					y: 100
				},
				tooltip: {
					formatter: this.getRiskChartTooltipFormatter()
				}
			}
		);
		this.hideChart = !this.contentService.getBoolean('SHOW_ASSET_ALLOCATION_PROJECTED_RISK_RETURN_CHART', true);
	}

	private getColumnClassesForTable(portfolioCount: number): DisplayTableColumnClassOverride {
		switch (portfolioCount) {
			case 2:
				return {
					categoryColumnClass: 'col-8 col-sm-24',
					dataColumnClass: 'col-8 col-sm-12'
				};
			default:
				return null;
		}
	}

	private getReturnChartTooltipFormatter(): Function {
		return function (): string {
			const tooltip =
				`<div class="tooltip">
				<div class="text-12"><span class="text-16" style="color:${this.point.series.color}">\u25CF</span> ${this.point.series.name}</div>
				<table>
					<colgroup>
						<col class="metric-name" />
						<col class="percent-amount" />
					</colgroup>
					<tr>
						<td class="text-12"><strong>Median</strong></td>
						<td class="text-12"><strong>${NumberUtil.formatWithPrecisionRounding(this.point.y, 2)}%</strong></td>
					</tr>
					<tr>
						<td class="text-12">Low</td>
						<td class="text-12">${NumberUtil.formatWithPrecisionRounding(this.point.series.userOptions.auxiliaryData?.Low, 2)}%</td>
					</tr>
					<tr>
						<td class="text-12">High</td>
						<td class="text-12">${NumberUtil.formatWithPrecisionRounding(this.point.series.userOptions.auxiliaryData?.High, 2)}%</td>
					</tr>
				</table>
			</div>`;

			return tooltip;
		};
	}

	private getRiskChartTooltipFormatter(): Function {
		return function (): string {
			const tooltip =
				`<div class="tooltip">
				<div class="text-12"><span class="text-16" style="color:${this.point.series.color}">\u25CF</span> ${this.point.series.name}</div>
				<table>
					<colgroup>
						<col class="metric-name" />
						<col class="percent-amount" />
					</colgroup>
					<tr>
						<td class="text-12">Risk</td>
						<td class="text-12">${NumberUtil.formatWithPrecisionRounding(this.point.y, 2)}%</td>
					</tr>
				</table>
			</div>`;

			return tooltip;
		};
	}

	private createTooltipAuxiliaryData(auxiliaryData: TabData): any[] {
		return auxiliaryData.tableData.map((singlePortfolioData) => {
			return singlePortfolioData.headersX.reduce((retVal, header, i) => {
				retVal[header.formattedValue] = 100 * Number(singlePortfolioData.cells[0][i].rawValue);
				return retVal;
			}, {});
		});

	}

	private getTickPositions(numberOfTicks: number): number[] {
		const cells = flattenDeep(this.chartDataTabs.map(tab => tab?.tableData?.[0]?.cells)).map(cell => (cell?.rawValue * 100));
		const maxData = Math.ceil(Math.max(...cells));
		const minData = Math.min(...cells) < 0 ? Math.min(...cells) : 0;
		const upperLimit = Math.ceil(maxData);
		const lowerLimit = Math.floor(minData);
		const tickAmount = (upperLimit - lowerLimit) / numberOfTicks;
		const result = range(numberOfTicks + 1).map(index => round(minData + index * tickAmount, 1));
		return result;
	}
}
